import React, { useEffect, useState, useCallback } from 'react';
import { Download, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api, inr, dt, StatusPill } from './adminApi';

const FILTERS = [['', 'All'], ['pending_payment', 'Payment Pending'], ['confirmed', 'New · In Queue'], ['packed', 'Packed'], ['shipped', 'On the Way'], ['delivered', 'Delivered'], ['cancelled', 'Cancelled'], ['refunded', 'Refunded']];

export const AdminOrders = ({ initial = {}, onOpen, reloadKey }) => {
  const [f, setF] = useState({ status: initial.status || '', q: initial.q || '', date_from: '', date_to: '' });
  const [data, setData] = useState({ total: 0, orders: [] });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.orders(f)); } finally { setLoading(false); }
  }, [f]);
  useEffect(() => { load(); }, [load, reloadKey]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="space-y-4" data-testid="admin-orders">
      <div className="flex flex-wrap gap-2" data-testid="order-status-filters">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setF({ ...f, status: k })} data-testid={`order-filter-${k || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${f.status === k ? 'bg-[var(--si-navy)] text-white border-[var(--si-navy)]' : 'bg-white border-[var(--si-line)] hover:border-[var(--si-navy)]'}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--si-grey)]" />
          <input value={f.q} onChange={set('q')} placeholder="Order ID, name, email, phone, payment ID" className="si-input !w-80 !py-2 !pl-9" data-testid="orders-search" />
        </label>
        <label className="text-xs font-semibold">From<input type="date" value={f.date_from} onChange={set('date_from')} className="si-input !py-2 block" data-testid="orders-date-from" /></label>
        <label className="text-xs font-semibold">To<input type="date" value={f.date_to} onChange={set('date_to')} className="si-input !py-2 block" data-testid="orders-date-to" /></label>
        <button onClick={load} className="si-btn si-btn-outline !py-2 !px-3 text-sm" data-testid="orders-refresh"><RefreshCw className="w-4 h-4" /></button>
        <button onClick={() => api.exportCsv(f).catch((e) => toast.error(e.message))} className="si-btn si-btn-primary !py-2 !px-4 text-sm ml-auto" data-testid="orders-export-csv"><Download className="w-4 h-4" /> Export CSV</button>
      </div>
      <p className="text-sm text-[var(--si-grey)]" data-testid="orders-count">{loading ? 'Loading…' : `${data.total} order${data.total === 1 ? '' : 's'}`}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]" data-testid="admin-orders-table">
          <thead><tr className="border-b border-[var(--si-line)] text-left text-xs uppercase tracking-wider text-[var(--si-grey)]">
            <th className="py-2">Order</th><th>Date</th><th>Customer</th><th>Items</th><th>Total</th><th>Payment</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} className="border-b border-[var(--si-line)] hover:bg-[var(--si-bg-soft)] cursor-pointer" onClick={() => onOpen(o.id)} data-testid={`order-row-${o.id}`}>
                <td className="py-2.5 font-mono font-bold">#{o.id.slice(0, 8).toUpperCase()}</td>
                <td className="text-xs">{dt(o.created_at)}</td>
                <td>{o.customer_name}<br /><span className="text-xs text-[var(--si-grey)]">{o.customer_phone} · {o.customer_email}</span></td>
                <td>{(o.items || []).reduce((n, it) => n + it.qty, 0)}</td>
                <td className="font-bold">{inr(o.total)}{o.refunded_amount > 0 && <span className="block text-xs text-[#B91C1C]">−{inr(o.refunded_amount)} refunded</span>}</td>
                <td className="text-xs capitalize">{(o.payment_status || '').replaceAll('_', ' ')}</td>
                <td><StatusPill s={o.status} /></td>
                <td><button className="text-[var(--si-blue)] font-semibold text-sm" data-testid={`order-open-${o.id}`}>Manage</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOrders;
